import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import useAppContext from "../AppContext";
import useUrlSlugContext from "../UrlSlugContext";
import { getSavedStateKey, saveAppState } from "../../utils/branching";
import routes from "../../routes";
import { saveDataToDb } from "../../utils/saveDataToDb";
import { cloneDeep } from "lodash";
import { redirectToFirstPage } from "../UrlValidator";

const AppState = ({ children }) => {
  const history = useHistory();
  const currentLocation = useLocation();
  const { baseUrl, queryString, setQueryString, urlSlug } = useUrlSlugContext();
  const {
    profile,
    setProfile,
    contact,
    setContact,
    newQuiz,
    assessment,
    answers,
    setAnswers,
    nextStack,
    setNextStack
  } = useAppContext();
  const [doRedirectFirstQuestion, setDoRedirectFirstQuestion] = useState(false);

  const isGoingBack = newLocation => {
    const currentPath = currentLocation.pathname.substring(1); // remove leading /
    const newPath = newLocation.pathname.substring(1);

    const currentPathIndex = assessment.questions.findIndex(
      q => q.urlPath === currentPath
    );
    const newPathIndex = assessment.questions.findIndex(
      q => q.urlPath === newPath
    );

    if (currentPathIndex > newPathIndex) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (history) {
      let unlisten = history.listen((location, action) => {
        // On moving to the next screen by next footer button only
        // (next browser button triggers POP)
        if (action === "PUSH") {
          saveAppState(
            baseUrl,
            location,
            answers,
            profile,
            contact,
            nextStack,
            queryString
          );
        } else if (action === "POP") {
          // On clicking app/browser back buttons and browser next button
          let state = window.sessionStorage.getItem(
            getSavedStateKey(location.pathname, baseUrl)
          );
          if (!state) {
            setDoRedirectFirstQuestion(true);
          } else {
            state = JSON.parse(state);
            setAnswers(state.answers);
            setProfile(state.profile);
            setContact(state.contact);
            setNextStack(state.nextStack);
            setQueryString(state.queryString);
            if (location.pathname !== routes.results) {
              if (isGoingBack(location)) {
                saveDataToDb(newQuiz, state.answers, assessment, true);
              } else {
                // Going forward, we want to send to MongoDB all answers except current one
                // In case of going backward, we always delete answer before new current one.
                const answersToSave = cloneDeep(state.answers);
                const lastAnswerQuestionId =
                  answersToSave[answersToSave.length - 1]?.questionId;
                const lastAnsweredQuestion = assessment.questions.find(
                  q => q._id === lastAnswerQuestionId
                );
                // We do not want to send to MongoDB the answer the user selected
                // on the page we are going forward to.
                if (
                  lastAnsweredQuestion?.urlPath ===
                  location.pathname.substring(1)
                ) {
                  answersToSave.pop();
                }

                saveDataToDb(newQuiz, answersToSave, assessment, false);
              }
            }
          }
        }
      });

      return () => {
        unlisten();
      };
    }
  }, [
    history,
    setDoRedirectFirstQuestion,
    answers,
    setAnswers,
    queryString,
    profile,
    setProfile,
    contact,
    setContact,
    setNextStack,
    JSON.stringify(newQuiz),
    assessment
  ]);

  if (doRedirectFirstQuestion) {
    redirectToFirstPage(urlSlug);
    return null;
  }

  return <>{children}</>;
};

export default AppState;
